import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WarningIcon from '@mui/icons-material/Warning';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ResponsiveDialog, {
  StyledButton,
  StyledLoadingButton,
} from '~/components/ResponsiveDialog';
import {
  generateVPSAppButtonTestId,
  getSelfManagedPlanName,
} from '~/Nova/scenes/HostingList/utils/helpers';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';

import AppLogo from '../../../../components/AppLogo';
import { useReimageMutation } from '../../../../hooks/useReimageMutation';
import {
  CHANGE_IMAGE,
  VPS_REIMAGE_ACTION_TYPE,
} from '../../../../utils/constants';
import { getLogoConfig } from '../../../../utils/imageLogos';

interface ReImageOSModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  osFilteredList: Record<string, PublicImageItem[]>;
  selectedOS: string;
  hostingId: string;
  isSelfManagedDedi?: boolean;
}

const ReImageOSModal = ({
  open,
  setOpen,
  selectedOS,
  osFilteredList,
  hostingId,
  isSelfManagedDedi = false,
}: ReImageOSModalProps) => {
  const { t } = useTranslation('hosting', {
    keyPrefix: 'hostingNova.selfManagedApps',
  });

  const [confirmationText, setConfirmationText] = useState('');
  const [selectedOSImageID, setSelectedOSImageID] = useState('');
  const logoConfig = getLogoConfig(selectedOS);
  const { logo, logoAlt } = logoConfig ?? { logo: '', logoAlt: '' };
  const osDisplayName = osFilteredList[selectedOS]?.[0]?.os_name ?? selectedOS;

  const selectedOSVersionsList = useMemo(() => {
    if (selectedOS !== undefined && selectedOS in osFilteredList) {
      const osList = osFilteredList[selectedOS] ?? [];
      // Sort by numeric os_version in descending order
      return [...osList].sort((a, b) => {
        const versionA = Number(a.os_version ?? 0);
        const versionB = Number(b.os_version ?? 0);
        return versionB - versionA;
      });
    }
    return [];
  }, [selectedOS, osFilteredList]);

  const selectedDefaultOSImageID = String(selectedOSVersionsList[0]?.id ?? '');
  const defaultOSVersion = `${osDisplayName} ${selectedOSVersionsList[0]?.os_version}`;

  const handleClose = () => {
    setConfirmationText('');
    setSelectedOSImageID('');
    setOpen(false);
  };

  const { mutate: handleOSReimage, isLoading: isLoadingOSReimage } =
    useReimageMutation({
      hostingId,
      onClose: handleClose,
    });

  const handleOSVersionChange = (imageId: string) => {
    setSelectedOSImageID(imageId);
  };

  const handleConfirmationTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmationText(e.target.value);
  };
  const handleProceed = () => {
    if (!selectedOSImageID && !selectedDefaultOSImageID) {
      return;
    }
    handleOSReimage({
      hostingId: hostingId,
      data: {
        action: VPS_REIMAGE_ACTION_TYPE,
        image_id: selectedOSImageID || selectedDefaultOSImageID,
      },
    });
  };

  const imageId = selectedOSImageID || selectedDefaultOSImageID;
  const isConfirmed =
    confirmationText.toLowerCase() === CHANGE_IMAGE && !!imageId;
  return (
    <ResponsiveDialog open={open} data-testid="reImage-os-modal">
      <DialogTitle>
        <Typography variant="h2">{t('reimageServer')}</Typography>
        <Divider sx={{ mt: 1 }} />
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Stack direction="column" spacing={3} sx={{ py: 1 }}>
          <AppLogo
            logoConfig={{
              key: selectedOS,
              name: osDisplayName,
              logo,
              logoAlt,
              size: {
                small: { width: 25, height: 25 },
                large: { width: 40, height: 38 },
              },
            }}
            variant="large"
          />
          <FormControl fullWidth>
            <InputLabel id="version-select-label">
              {t('versionLabel')}
            </InputLabel>
            <Select
              label={t('versionLabel')}
              required
              defaultValue={defaultOSVersion}
              size="small"
            >
              {selectedOSVersionsList?.map(
                ({ id, os_name, os_version, lts }) => (
                  <MenuItem
                    key={id}
                    value={`${osDisplayName} ${os_version}`}
                    onClick={() => handleOSVersionChange(String(id ?? ''))}
                  >
                    {`${osDisplayName} ${os_version} ${
                      Number(lts) === 1 && os_name === 'Ubuntu'
                        ? t('osLts')
                        : ''
                    }`}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="column" spacing={3} sx={{ pt: 2 }}>
          <Stack direction="row" spacing={1}>
            <WarningIcon color="error" />
            <Typography>
              <Typography component="span" color="error">
                {t('modal.warning')}
              </Typography>{' '}
              {t('modal.warningTextOS', {
                hostingPlanType: getSelfManagedPlanName(isSelfManagedDedi),
              })}
            </Typography>
          </Stack>
          <TextField
            fullWidth
            label={t('modal.changeImageConfirmationText')}
            value={confirmationText}
            onChange={handleConfirmationTextChange}
            variant="outlined"
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          spacing={2}
          flexGrow={1}
          justifyContent="flex-end"
        >
          <StyledButton
            variant="outlined"
            onClick={handleClose}
            data-testid={generateVPSAppButtonTestId(osDisplayName, 'cancel')}
          >
            {t('modal.cancel')}
          </StyledButton>
          <StyledLoadingButton
            data-testid={generateVPSAppButtonTestId(osDisplayName, 'proceed')}
            disabled={!isConfirmed || isLoadingOSReimage}
            loading={isLoadingOSReimage}
            onClick={handleProceed}
            color="error"
          >
            {t('modal.proceed')}
          </StyledLoadingButton>
        </Stack>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ReImageOSModal;
